<template>
  <div :class="['bd-table', { 'bd-table--scrolly': !config.disableYScroll }]" ref="tableRef">
    <slot name="top" v-bind="{ data: originSource }" />
    <ConditionBox v-if="config.conditions?.length" :config="config.conditions" @change="handleConditionChange" @expand="calcTableScroll" />
    <div class="bd-table__header">
      <div class="bd-table__header__left">
        <Button
          v-if="config.createAction"
          v-permission="config.createAction?.permission"
          type="primary"
          @click="handleAdd"
          v-bind="config.createAction?.attrs"
        >
          <template #icon><PlusOutlined /></template>
          {{ config.createAction?.title ?? '新建' }}
        </Button>
        <slot name="header" v-bind="{ params: state.queryParams }" />

        <MutipleSelectBox
          v-if="config?.mutipleAction"
          v-model:keys="rowSelection.selectedRowKeys"
          v-model:dataSource="rowSelection.selectedRowRecords"
          :actions="config.mutipleAction"
          :reload-fn="handleMutipleReload"
        />
      </div>
      <div v-if="!config.isControlHidden" class="bd-table__header__right">
        <TableSize v-model:size="state.size" @change="calcTableScroll" />
        <CustomTableHeader v-model:checks="checks" v-model:columns="columns" @reset="resetColums" />
        <Refresh @reload="getData" />
      </div>
    </div>
    <slot name="loft" v-bind="{ params: state.queryParams }" />
    <div ref="tableBodyRef" class="bd-table__body" :class="{ hideno: config.enableLastId }">
      <Table
        :size="state.size"
        :data-source="state.dataSource"
        :columns="columnsShow"
        :loading="state.loading"
        :pagination="pagination"
        :row-key="config.key ?? 'id'"
        :scroll="tableScroll"
        v-bind="$attrs"
        :row-class-name="($attrs?.rowClassName as string) ?? ((_, index) => (index % 2 === 1 ? 'bd-table__striped' : undefined))"
        :row-selection="$attrs?.rowSelection ?? (config.mutipleAction ? rowSelection : undefined)"
        :footer="config.footer ? source => config.footer?.(source, originSource) : undefined"
        @change="handleTableChange"
      >
        <template v-if="$slots.expandedRowRender" #expandedRowRender="{ record, index, indent, expanded }">
          <slot name="expandedRowRender" v-bind="{ record, index, indent, expanded }" />
        </template>
        <template #bodyCell="{ text, column, record }">
          <template v-if="column.dataIndex === 'operation' && !column.customRender">
            <div class="bd-table__action">
              <slot name="actionExtra" v-bind="{ record }" />
              <Button
                v-if="config.updateAction"
                v-permission="config.updateAction?.permission"
                type="link"
                size="small"
                @click="handleEdit(record)"
                :disabled="config.updateAction?.disabled?.(record)"
                v-bind="config.updateAction?.attrs"
              >
                {{ config.updateAction?.title ?? '编辑' }}
              </Button>
              <Dropdown v-if="!config.isActionMoreHidden" :trigger="['click']" :disabled="moreActionDisabled" :getPopupContainer="getPopupContainer">
                <Button class="operationBtn" type="link" size="small">
                  更多
                  <DownOutlined />
                </Button>
                <template #overlay>
                  <Menu>
                    <slot name="action" v-bind="{ record }" />
                    <Menu.Item
                      v-if="config.deleteAction && $permission(config.deleteAction?.permission)"
                      key="del"
                      dangered
                      :disabled="config.deleteAction?.disabled?.(record)"
                      v-bind="config.deleteAction?.attrs"
                      @click="handleDel(record)"
                    >
                      {{ config.deleteAction?.title ?? '删除' }}
                    </Menu.Item>
                  </Menu>
                </template>
              </Dropdown>
            </div>
          </template>
          <!-- 排序 -->
          <template v-else-if="column.dataIndex === 'order' && !column.customRender">
            <OrderCell v-if="$permission(config.orderAction?.permission)" :record="record" :text="text" :order-fn="config.orderAction" />
            <span v-else>{{ text }}</span>
          </template>
          <!-- 状态 -->
          <template v-else-if="column.dataIndex === 'isOpen' && !column.customRender">
            <StateCell
              v-if="$permission(config.disableAction?.permission) && $permission(config.enableAction?.permission)"
              :record="record"
              :text="text"
              :enable-fn="config.enableAction"
              :disable-fn="config.disableAction"
            />
            <StateTagCell v-else :record="record" :text="text" />
          </template>
        </template>
      </Table>
    </div>
    <DialogForm
      v-if="dialogFormSchema.length > 0"
      v-model:visible="dialogFormState.visible"
      :disable-cache="config.disableCache"
      v-bind="config.formAttrs"
      :model="dialogFormModel"
      :title="dialogFormTitle"
      :schema="dialogFormSchema"
      :confirm-fn="handleSubmit"
      :deep-watch="false"
    />
  </div>
</template>

<script lang="ts">
import * as Comps from './components'
import { PlusOutlined, DownOutlined } from '@ant-design/icons-vue'
import { DEFAULT_INTERVAL } from './constant'

export default {
  name: 'BdTable',
  components: { ...Comps, PlusOutlined, DownOutlined }
}
</script>
<script lang="ts" setup>
import { inject, watch, reactive, shallowRef, onMounted, onBeforeUnmount, computed } from 'vue'
import { Table, Button, Menu, Dropdown } from 'ant-design-vue'
import { BdTableProps } from './props'
import { DialogForm } from '-/Base/DialogForm'
import { ConditionBox } from '-/Base/ConditionBox'
import useColumns from './hooks/useColumns'
import { registerDialogForm } from './hooks/useDialogForm'
import useTableUi from './hooks/useTableUi'
import useOrder from './hooks/useOrder'
import useConditions from './hooks/useConditions'
import useMutipleSelect from './hooks/useMutipleSelect'
import { isArray, isEmpty } from '@bd/vue3-utils'
const props = defineProps(BdTableProps)
const lastIds: string[] = []
interface StateType {
  loading: boolean
  size: 'small' | 'middle' | 'large' | undefined
  dataSource: any[]
  queryParams?: any
}
const getPopupContainer = () => {
  if (props.config?.disableYScroll) return document.body
  return document.querySelector('.ant-table-body') || document.body
}
const mitter = inject('mitter') as any
const originSource = shallowRef()
const state = reactive<StateType>({
  loading: false,
  size: 'large',
  dataSource: []
})
const _getPaginationState = () => ({
  current: 1,
  pageSize: props.config?.pageSize ?? 20,
  total: 0,
  showSizeChanger: true,
  showTotal(total) {
    const res: string[] = [`总计 ${total} 条`]
    if (props.config.enableLastId) res.unshift(`第 ${pagination.current} / ${Math.ceil(total / pagination.pageSize)} 页`)
    return res.join(' | ')
  }
})
const pagination = props.config.pagination ?? reactive<any>(_getPaginationState())
const resetPagination = () => {
  if (props.config.pagination !== false) {
    pagination.current = 1
  }
}
const { handleOrders, getOrders } = useOrder(props.config.columns)
const { handleConditionChange, getConditions } = useConditions(resetPagination, props.config?.conditions, props.config?.useEasyConditions)
const { rowSelection, moreActionDisabled } = useMutipleSelect()

const { tableScroll, tableBodyRef, tableRef, calcTableScroll } = useTableUi({
  disableXScroll: props.config.disableXScroll,
  disableYScroll: props.config.disableYScroll,
  size: computed(() => state.size),
  pagination: props.config.pagination
})
const handleTableChange = (...args) => {
  const [_pagination, _, sorter] = args
  Object.assign(pagination, {
    current: _pagination.current,
    pageSize: _pagination.pageSize
  })
  handleOrders(isArray(sorter) ? sorter : isEmpty(sorter) ? [] : [sorter])
}
const { columns, checks, columnsShow, resetColums } = useColumns(props.config)
const _generParams = () => {
  let res: any = {}
  if (pagination !== false) {
    if (props.config?.enableLastId) {
      res.limit = pagination.pageSize
      res.marker = lastIds[pagination.current! - 1]
    } else {
      res = {
        page: pagination.current,
        pageSize: pagination.pageSize
      }
    }
  }
  if (props.config.conditions) {
    const _conditions = getConditions()
    if (props.config.useEasyConditions) {
      if (Object.keys(_conditions).length) {
        if (props.config.expandConditions) {
          res = { ...res, ..._conditions }
        } else {
          res.conditions = _conditions
        }
      }
    } else {
      _conditions.length ? (res.conditions = _conditions) : ''
    }
  }
  res.orders = getOrders()
  if (props.config.beforeGetFn) res = props.config.beforeGetFn(res)
  return res
}
const handleMutipleReload = async () => {
  rowSelection.selectedRowKeys = []
  await getData()
}
let timer: any = null
const clearTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}
const getData = async (fromPoll = false) => {
  if (!fromPoll) state.loading = true
  try {
    if (props.config.enablePollGet && !timer) {
      timer = setInterval(
        () => {
          getData(true)
        },
        props.config?.pollGetInterval ?? DEFAULT_INTERVAL
      )
    }
    const _params = _generParams()
    state.queryParams = _params
    const data = await props.config.getFn(_params)
    if (pagination !== false) {
      if (props.config.enableLastId) {
        lastIds[pagination.current!] = data.data[data.data.length - 1]?.id
      }
      pagination.total = data.total
    }
    originSource.value = data
    if (props.config.afterGetFn) {
      state.dataSource = props.config.afterGetFn(data)
    } else {
      state.dataSource = props.config.pagination === false ? data ?? [] : data?.data ?? []
    }
    state.loading = false
  } catch (err) {
    clearTimer()
    state.loading = false
    return Promise.reject(err)
  }
}

const emits = defineEmits(['refresh'])
watch(
  () => _generParams(),
  () => getData(),
  { deep: true, immediate: true }
)
const {
  state: dialogFormState,
  title: dialogFormTitle,
  schema: dialogFormSchema,
  handleSubmit,
  handleDel,
  handleEdit,
  handleAdd,
  dialogFormModel,
  isEdit
} = registerDialogForm(props.config, getData, emits)
onMounted(() => {
  mitter.on('TABLE_REFRESH', () => {
    emits('refresh')
    getData()
  })
})

onBeforeUnmount(() => {
  mitter.off('TABLE_REFRESH')
  clearTimer()
})
defineExpose({ dialogFormState, state, originSource, isEdit })
</script>
<style lang="less">
.bd-table {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: @border-radius-base;
  background: @white;

  // &--scrolly {
  //   .ant-table-body {
  //     position: relative;
  //   }
  // }
  &__striped {
    background: rgba(249, 250, 255, 0.69);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    padding-bottom: @padding-md;

    &__left,
    &__right {
      display: flex;
      gap: @margin-sm;
    }
  }

  &__body {
    flex: 1;

    &.hideno {
      .ant-pagination-item {
        display: none;
      }
    }
  }

  &__action {
    display: flex;
  }
}
</style>
